import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import get from 'lodash/get'
import flatten from 'lodash/flatten'
import shuffle from 'lodash/shuffle'

import { getRecommendationTree } from '@/assets/recommendations'

// Frozen: a plain object here is a Node-process-wide singleton (shared across every SSR request this
// module's process handles), so this locks in that it's never accidentally reassigned rather than relying
// on convention.
export const recommendationTemplateType = Object.freeze({
  START: 'start',
  QUESTIONNAIRE: 'questionnaire',
  SUGGESTIONS: 'suggestions',
  LOADING: 'loading',
})

function getQuestionContent(questions: any, selectionPath: any, index: number) {
  const questionObj = get(questions, selectionPath[index]?.[0], {})
  return questionObj.content_en || ''
}

function getAnswerContent(answers: any, selectionPath: any, index: number) {
  const answerObj = get(answers, selectionPath[index]?.[1], {})
  return answerObj.answer_en || ''
}

/**
 * Keuzehulp (recommendation questionnaire) state. GTM callbacks are passed in by the caller and
 * forwarded unchanged.
 *
 * The whole store is persisted to sessionStorage, so wizard progress survives a reload within the same
 * tab. `piniaPluginPersistedstate` is Nuxt-auto-imported by the `pinia-plugin-persistedstate/nuxt`
 * module; its `sessionStorage()` storage is SSR-safe (no-ops on the server).
 */
export const useRecommendationsStore = defineStore(
  'recommendations',
  () => {
    const cacheStore = useCacheStore()
    const bridgeFetch = useBridgeFetch()
    const graphqlFetch = useGraphqlFetch()
    const { locale } = useI18n()

    const tree = ref<any>(null)
    const treeIndex = ref<any>(null)
    const questions = ref<any>(null)
    const answers = ref<any>(null)
    const selection = ref<any>({})
    const selectionPath = ref<any[]>([])
    const activeTemplate = ref<string | null>(null)
    const recommendationEvents = ref<any>({})
    const recommendationEventUris = ref<any[]>([])
    const userScoreId = ref<any>(null)
    const scoreHash = ref<any>(null)

    function setRecommendationTree({ questions: newQuestions, answers: newAnswers, tree: newTree }: { questions: any; answers: any; tree: any }) {
      questions.value = newQuestions
      answers.value = newAnswers
      tree.value = newTree
    }
    function setRecommendationTreeIndex({ index }: { index: any }) {
      treeIndex.value = index
    }
    function initSelectionPath() {
      selectionPath.value = [[Object.keys(tree.value)[0]]]
    }
    function setSelection(gtmRecommendationEnd?: (_payload: any) => void) {
      const currentPath = selectionPath.value
      const lastNode = currentPath[currentPath.length - 1]
      // Use selectionPath to find the current question key
      if (lastNode.length > 1) {
        const treePath = flatten(currentPath)
        const [questionKey] = Object.keys(get(tree.value, treePath, null) || {})

        if (!questionKey) {
          getRecommendations({ selectionPath: currentPath, gtmRecommendationEnd })
          return
        }

        selectionPath.value.push([questionKey])
      }

      const [questionKey] = currentPath[currentPath.length - 1]
      const question = questions.value[questionKey]

      const treePath = flatten(currentPath)
      // `get` only falls back to its default on `undefined`, so a `null` leaf in the tree comes straight
      // through and `Object.keys` throws. Same guard as the other tree lookups in this store.
      const answersKeys = Object.keys(get(tree.value, treePath, tree.value) || {})
      let selectionAnswers = answersKeys.map((key) => ({
        ...answers.value[key],
        id: key,
        answer: answers.value[key][`answer_${locale.value}`],
        description: answers.value[key][`description_${locale.value}`],
      }))

      // Randomize the order of answers for the 'architectuur' question only
      if (questionKey === 'architectuur') {
        selectionAnswers = shuffle(selectionAnswers)
      }

      selection.value = {
        question: {
          ...question,
          question: question[`content_${locale.value}`],
          description: question[`description_${locale.value}`],
        },
        answers: selectionAnswers,
      }
    }
    function setActiveTemplate(templateType: string) {
      activeTemplate.value = templateType
    }
    function reset() {
      selectionPath.value = []
      userScoreId.value = null
      scoreHash.value = null
      activeTemplate.value = recommendationTemplateType.START
    }
    function back() {
      if (selectionPath.value.length > 1) {
        // Remove last question entry
        selectionPath.value.pop()
        // Remove answer of previous question
        const lastIndex = selectionPath.value.length - 1
        selectionPath.value[lastIndex].pop()
      } else {
        activeTemplate.value = recommendationTemplateType.START
      }
    }
    function next(answerId: any) {
      const lastIndex = selectionPath.value.length - 1
      selectionPath.value[lastIndex].push(answerId)
    }
    function setRecommendationEventUris(eventUris: any[]) {
      recommendationEventUris.value = eventUris
    }
    function setRecommendationEvents(events: any) {
      recommendationEvents.value = events
    }
    function setUserScoreId(id: any) {
      userScoreId.value = id
    }
    function setScoreHash(hash: any) {
      scoreHash.value = hash
    }

    async function loadRecommendationData() {
      /*
       * `static/` was renamed to `public/` in the Nuxt 3 migration; public files are served at the site
       * root and are not module-importable, so the JSON is fetched at runtime (works both SSR and client)
       * instead of `import('@/static/keuzehulp_input.json')`.
       */
      const { questions: loadedQuestions, answers: loadedAnswers, trees } = await $fetch<any>('/keuzehulp_input.json')

      const { tree: loadedTree, treeIndex: loadedTreeIndex } = getRecommendationTree(trees || [])

      if (!loadedQuestions || !loadedAnswers || !loadedTree) {
        throw new Error('Could not load recommendation tree. Ensure questions, answers, and tree data are available.')
      }

      setRecommendationTree({
        questions: loadedQuestions,
        answers: loadedAnswers,
        tree: loadedTree,
      })
      setRecommendationTreeIndex({ index: loadedTreeIndex })
      setActiveTemplate(recommendationTemplateType.START)
    }

    async function getRecommendations({ selectionPath: currentSelectionPath, gtmRecommendationEnd }: { selectionPath: any[]; gtmRecommendationEnd?: (_payload: any) => void }) {
      try {
        setActiveTemplate(recommendationTemplateType.LOADING)
        const key = `recommendations;${locale.value};${currentSelectionPath.map((path) => path.join(':')).join(';')}`
        const ttl = 900
        let recommendationEventsIds: any = {}

        const fetcher = async () => {
          recommendationEventsIds = await bridgeFetch('/recommendations', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: JSON.stringify(
              currentSelectionPath.reduce((acc, [questionKey, answerKey]) => {
                acc[questionKey] = answerKey
                return acc
              }, {}),
            ),
          })
          setRecommendationEvents(recommendationEventsIds)

          const sortedKeys = Object.entries(recommendationEventsIds?.recommended_concerts || {})
            .sort((a, b) => (b[1] as number) - (a[1] as number))
            .map((entry) => entry[0])

          const { hitQuery } = await graphqlFetch({
            token: 'elasticsearch',
            query: `query recommendationsEventSearch($importIconcertIdentifier: [String], $site: [String]) {
              hitQuery: elasticSearch(importIconcertIdentifierFacet: $importIconcertIdentifier, site: $site section: "event") {
                count
                hits(limit: 1024){
                  uri
                  importIconcertIdentifier
                  eventDate
                }
              }
            }
          `,
            variables: {
              importIconcertIdentifier: sortedKeys,
              site: `${locale.value}Default`,
            },
          })

          return hitQuery.hits.sort((a: any, b: any) => sortedKeys.indexOf(a.importIconcertIdentifier) - sortedKeys.indexOf(b.importIconcertIdentifier))
        }

        const hits = await cacheStore.fetch({ key, ttl, fetcher, fallback: [] })
        const recommendationEventUrisResult = Array.isArray(hits) ? hits : []

        setRecommendationEventUris(recommendationEventUrisResult)
        setActiveTemplate(recommendationTemplateType.SUGGESTIONS)

        if (gtmRecommendationEnd) {
          const questionContentField = `content_${locale.value}`
          const answerContentField = `answer_${locale.value}`
          const currentQuestions = questions.value || {}
          const currentAnswers = answers.value || {}

          const keuzehulpAnswers = currentSelectionPath
            .map(([questionKey, answerKey]) => {
              const question = currentQuestions[questionKey]
              const answer = currentAnswers[answerKey]

              if (!question || !answer) {
                return null
              }

              return {
                question: question[questionContentField] || question.content_en || '',
                answer: answer[answerContentField] || answer.answer_en || '',
              }
            })
            .filter((entry) => entry !== null)

          const recommendationScores = Object.keys(recommendationEventsIds || {}).length > 0 ? recommendationEventsIds : recommendationEvents.value || {}
          const recommendedConcertScores = recommendationScores?.recommended_concerts || {}
          const recommendedConcerts = Object.entries(recommendedConcertScores)
            .sort(([, scoreA], [, scoreB]) => (scoreB as number) - (scoreA as number))
            .map(([concertId, score], index) => ({
              concert_id: String(concertId),
              score,
              rank: index + 1,
            }))

          const keuzehulpScores = Object.fromEntries(Object.entries(recommendationScores).filter(([key]) => key !== 'recommended_concerts'))

          gtmRecommendationEnd({
            keuzehulpAnswers,
            keuzehulpScores,
            recommendedConcerts,
          })
        }
      } catch (e) {
        console.error('Error in getRecommendations:', e)
        reset()
      }
    }

    async function storeUserScores({ recommendationEvents: recommendationEventsPayload, user, email }: { recommendationEvents: any; user?: any; email?: string }) {
      try {
        if (!recommendationEventsPayload?.recommended_concerts || Object.keys(recommendationEventsPayload.recommended_concerts).length === 0) {
          return
        }

        const givenAnswers = selectionPath.value.reduce(
          (acc, [questionKey, answerKey]) => {
            if (questionKey && answerKey) {
              acc[questionKey] = answerKey
            }

            return acc
          },
          {} as Record<string, any>,
        )

        const userScoreResponse = await bridgeFetch('/user-score/save', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: JSON.stringify({
            userScore: recommendationEventsPayload,
            givenAnswers,
            ...(email && { email }),
            ...(user?.id && user?.hash && { userId: user.id, userHash: user.hash }),
          }),
        })
        setUserScoreId(userScoreResponse.id)
        setScoreHash(userScoreResponse.scoreHash)
      } catch (e) {
        console.error('Error in storeUserScores:', e)
      }
    }

    async function updateUserScores({ userScoreId: id, scoreHash: hash, email }: { userScoreId: any; scoreHash: any; email?: string }) {
      try {
        await bridgeFetch('/user-score/update', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: JSON.stringify({
            id,
            scoreHash: hash,
            email,
          }),
        })
      } catch (e) {
        console.error('Error in storeUserScores:', e)
      }
    }

    function updateGtmRecommendationInteraction(gtmRecommendationInteraction: (_question: string, _answer: string, _nextQuestion: string, _count: number) => void) {
      const currentPath = selectionPath.value
      const lastNode = currentPath[currentPath.length - 1]

      // Check if the next question is not changing
      if (lastNode.length > 1) {
        const treePath = flatten(currentPath)
        const [questionKey] = Object.keys(get(tree.value, treePath, null) || {})
        // If there is no next question key, it means we are at the end of the questionnaire
        if (!questionKey) {
          // Send the last question and answer to GTM instead of the second to last question & answer
          const question = getQuestionContent(questions.value, selectionPath.value, selectionPath.value.length - 1)
          const answer = getAnswerContent(answers.value, selectionPath.value, selectionPath.value.length - 1)
          gtmRecommendationInteraction(question, answer, 'Results', currentPath.length)
          return
        }
      }

      const question = getQuestionContent(questions.value, selectionPath.value, selectionPath.value.length - 2)
      const answer = getAnswerContent(answers.value, selectionPath.value, selectionPath.value.length - 2)
      const nextQuestion = getQuestionContent(questions.value, selectionPath.value, selectionPath.value.length - 1)
      const answeredQuestionsAmount = selectionPath.value.length - 1

      gtmRecommendationInteraction(question, answer, nextQuestion, answeredQuestionsAmount)
    }

    function updateGtmRecommendationAudioInitialization(gtmRecommendationAudioInit: (_text: string) => void) {
      // Get the current active question
      const treePath = flatten(selectionPath.value)
      const question = get(questions.value, selectionPath.value[selectionPath.value.length - 1]?.[0], {})

      // Get the current active answers
      const answersKeys = Object.keys(get(tree.value, treePath, tree.value) || {})
      const activeAnswers = answersKeys.map((key) => answers.value[key])

      // Check if the question contains audio and initialize
      if (question?.sound) {
        gtmRecommendationAudioInit(question.content_en)
      }

      // Check if any answer contains audio and initialize
      const answersWithAudio = activeAnswers.filter((answer) => answer?.sound).map((answer) => answer.answer_en)
      if (answersWithAudio.length > 0) {
        gtmRecommendationAudioInit(answersWithAudio.join(', '))
      }
    }

    return {
      tree,
      treeIndex,
      questions,
      answers,
      selection,
      selectionPath,
      activeTemplate,
      recommendationEvents,
      recommendationEventUris,
      userScoreId,
      scoreHash,
      setRecommendationTree,
      setRecommendationTreeIndex,
      initSelectionPath,
      setSelection,
      setActiveTemplate,
      reset,
      back,
      next,
      setRecommendationEventUris,
      setRecommendationEvents,
      setUserScoreId,
      setScoreHash,
      loadRecommendationData,
      getRecommendations,
      storeUserScores,
      updateUserScores,
      updateGtmRecommendationInteraction,
      updateGtmRecommendationAudioInitialization,
    }
  },
  { persist: { storage: piniaPluginPersistedstate.sessionStorage() } },
)
